import { BrandKey, getBrandConfig } from '../config/brands';

type StorageScope =
  | 'brand_kit'
  | 'brand_kit_draft'
  | 'sitemap_data'
  | 'voc_kit'
  | 'voc_kit_draft'
  | 'voc_kit_ready_dismissed'
  | 'blog_agents_progress'
  | 'cta_creator_results'
  | 'cta_creator_inputs'
  | 'linkedin_prompts_draft'
  | 'linkedin_posts'
  | 'playbook_prompts_draft'
  | 'email_newsletters'
  | 'paa_trees'
  | 'rewrite_blog_posts_progress'
  | 'rewrite_blog_dataset'
  | 'generated_comments'
  | 'content_cache'
  | 'reddit_analysis_cache'
  | 'competitor_workflow'
  | 'competitor_meta'
  | 'competitor_prompts_draft'
  | 'competitor_ctas'
  | 'blog_content_draft'
  | 'blog_prompts_draft'
  | 'blog_meta'
  | 'best_of_articles_progress';

interface StorageOptions {
  itemId?: string;
  version?: string;
  legacyKeys?: string[];
  storage?: Storage;
}

export const BRAND_STORAGE_UPDATED_EVENT = 'brand-storage-updated';

/**
 * Build a brand-scoped storage key.
 * Why this matters: every stored payload needs a deterministic namespace so one
 * brand cannot overwrite or restore another brand's data.
 */
export const getBrandStorageKey = (
  brandKey: BrandKey,
  scope: StorageScope,
  options: Omit<StorageOptions, 'legacyKeys' | 'storage'> = {},
): string => {
  const { storagePrefix } = getBrandConfig(brandKey);
  const parts = [storagePrefix, scope];

  if (options.itemId) {
    parts.push(options.itemId);
  }

  if (options.version) {
    parts.push(options.version);
  }

  return parts.join('_');
};

/**
 * Read a brand-scoped value and migrate legacy keys when needed.
 * Why this matters: this is the bridge that migrates legacy US Citrus / USCN-prefixed
 * keys into the current brand-scoped namespace without breaking existing saved work.
 */
export const getBrandStorageItem = (
  brandKey: BrandKey,
  scope: StorageScope,
  options: StorageOptions = {},
): string | null => {
  const storage = options.storage ?? window.localStorage;
  const currentKey = getBrandStorageKey(brandKey, scope, options);
  const currentValue = storage.getItem(currentKey);

  if (currentValue !== null) {
    return currentValue;
  }

  for (const legacyKey of options.legacyKeys ?? []) {
    const legacyValue = storage.getItem(legacyKey);
    if (legacyValue !== null) {
      storage.setItem(currentKey, legacyValue);
      storage.removeItem(legacyKey);

      window.dispatchEvent(
        new CustomEvent(BRAND_STORAGE_UPDATED_EVENT, {
          detail: {
            brandKey,
            scope,
            key: currentKey,
            migratedFrom: legacyKey,
          },
        }),
      );

      return legacyValue;
    }
  }

  return null;
};

/**
 * Persist a brand-scoped value.
 * Why this matters: wrapping writes lets the rest of the codebase stop caring
 * about raw key strings while still broadcasting storage updates for listeners.
 */
export const setBrandStorageItem = (
  brandKey: BrandKey,
  scope: StorageScope,
  value: string,
  options: Omit<StorageOptions, 'legacyKeys'> = {},
): string => {
  const storage = options.storage ?? window.localStorage;
  const storageKey = getBrandStorageKey(brandKey, scope, options);

  storage.setItem(storageKey, value);
  window.dispatchEvent(
    new CustomEvent(BRAND_STORAGE_UPDATED_EVENT, {
      detail: {
        brandKey,
        scope,
        key: storageKey,
      },
    }),
  );

  return storageKey;
};

/**
 * Remove a brand-scoped value.
 * Why this matters: cleanup actions should target only the active brand's state
 * rather than clearing data for every site.
 */
export const removeBrandStorageItem = (
  brandKey: BrandKey,
  scope: StorageScope,
  options: Omit<StorageOptions, 'legacyKeys'> = {},
): void => {
  const storage = options.storage ?? window.localStorage;
  storage.removeItem(getBrandStorageKey(brandKey, scope, options));
};

export type { StorageScope };
